
import React, { useEffect } from 'react';
import AdminLayout from "@/components/layout/AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WebhookEventTypes } from "@/components/webhooks/WebhookEventTypes";
import { WebhookEndpoints } from "@/components/webhooks/WebhookEndpoints";
import { WebhookPayloadTemplates } from "@/components/webhooks/WebhookPayloadTemplates";
import { WebhookTriggers } from "@/components/webhooks/WebhookTriggers";
import { WebhookQueue } from "@/components/webhooks/WebhookQueue";
import { WebhookLogs } from "@/components/webhooks/WebhookLogs";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export default function WebhookManagement() {
  // Check if we need to create the digitization.completed event type
  const { data: eventTypes } = useQuery({
    queryKey: ['webhook-event-types-check'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_event_types')
        .select('*')
        .eq('event_code', 'digitization.completed');
      
      if (error) throw error;
      return data;
    },
  });

  // Create the digitization.completed event type if it doesn't exist
  useEffect(() => {
    const createDigitizationCompletedEvent = async () => {
      if (eventTypes && eventTypes.length === 0) {
        try {
          await supabase
            .from('webhook_event_types')
            .insert({
              event_code: 'digitization.completed',
              description: 'Triggered when a digitization request is completed',
              available_variables: {
                request_id: 'The ID of the digitization request',
                title: 'The title of the digitized artwork',
                creator: {
                  id: 'Creator ID',
                  name: 'Creator\'s first name',
                  surname: 'Creator\'s last name',
                  nick_name: 'Creator\'s nickname (if any)'
                },
                completed_at: 'Timestamp when the digitization was marked as completed',
                notes: 'Any notes added during completion'
              },
              is_active: true
            });
        } catch (error) {
          console.error('Error creating digitization.completed event type:', error);
        }
      }
    };

    createDigitizationCompletedEvent();
  }, [eventTypes]);

  return (
    <AdminLayout>
      <div className="space-y-4">
        <h1 className="text-3xl font-bold">Webhook Management</h1>
        <p className="text-muted-foreground">
          Configure and manage webhook integrations for the Artuba platform.
        </p>
        
        <Tabs defaultValue="event-types" className="w-full">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="event-types">Event Types</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
            <TabsTrigger value="triggers">Triggers</TabsTrigger>
            <TabsTrigger value="queue">Queue</TabsTrigger>
            <TabsTrigger value="logs">Logs</TabsTrigger>
          </TabsList>
          
          <TabsContent value="event-types" className="mt-6">
            <WebhookEventTypes />
          </TabsContent>
          
          <TabsContent value="templates" className="mt-6">
            <WebhookPayloadTemplates />
          </TabsContent>
          
          <TabsContent value="endpoints" className="mt-6">
            <WebhookEndpoints />
          </TabsContent>
          
          <TabsContent value="triggers" className="mt-6">
            <WebhookTriggers />
          </TabsContent>
          
          <TabsContent value="queue" className="mt-6">
            <WebhookQueue />
          </TabsContent>
          
          <TabsContent value="logs" className="mt-6">
            <WebhookLogs />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
